import { computed, ref } from 'vue';
import { storeToRefs } from 'pinia';
import { useI18n } from 'vue-i18n';
import { useMarketFeedStore } from '@/stores/marketFeed.js';
import { mapFeedCardsForPresentation as mapFeedCards } from '@/modules/market-feed/presentation/feed-card-display.js';
import { enrichFeedCardsForDisplay } from '@/composables/enrichFeedCardsForDisplay.js';
import { useFeedFavoriteSync } from '@/composables/useFeedFavoriteSync.js';
import { useMarketWatchlist } from '@/composables/useMarketWatchlist.js';
import { useFeedNetworkPresentation } from '@/modules/market-feed/presentation/useFeedNetworkPresentation.js';
import {
  FEED_CRYPTO_MINUTE_CATEGORY_KEY,
  isSportFeedRow,
} from '@/domain/market/feed-list-category.js';
import { isHomeHiddenIntervalUpdownRow } from '@/domain/crypto/crypto-minute-feed-pagination.js';

/** 跨组件实例共享，避免 remount 后 cachedCards 丢失导致「有数据却空白」 */
const feedCardsCache = ref([]);

/**
 * 首页展示过滤：
 * - 加密分钟盘 tab：排除体育行
 * - 「全部」：仅隐藏非 BTC 的 5m updown；其它周期/类型与 Crypto 频道不变
 */
function filterHomeFeedRows(rows, primaryCategory) {
  const category = String(primaryCategory ?? 'all').trim();
  const list = Array.isArray(rows) ? rows : [];
  if (category === FEED_CRYPTO_MINUTE_CATEGORY_KEY) {
    return list.filter((row) => !isSportFeedRow(row));
  }
  if (category === 'all') {
    return list.filter((row) => !isHomeHiddenIntervalUpdownRow(row));
  }
  return list;
}

/** 接口分类原文展示；仅「全部」为前端固定 tab */
function buildHomeCategoryChips(categories, t) {
  const apiChips = (Array.isArray(categories) ? categories : []).map((cat) => ({
    slug: String(cat.slug ?? '').trim(),
    label: String(cat.name || cat.slug || '').trim(),
  }));

  return [{ slug: 'all', label: t('markets.categories.all') }, ...apiChips];
}

function isDefaultFeedView(state) {
  return (
    String(state.primaryCategory ?? 'all').trim() === 'all' &&
    !String(state.appliedQuestion ?? '').trim()
  );
}

/**
 * 首页 Feed 展示层：统一 ViewModel + i18n 映射。
 * store 只保留 rawRows 与业务状态。
 */
export function useMarketFeedPresentation() {
  const feedStore = useMarketFeedStore();
  const { t, te, locale } = useI18n();
  const storeRefs = storeToRefs(feedStore);
  const { isWatchlisted, watchlistIds } = useMarketWatchlist();

  const cards = computed(() => {
    void locale.value;
    void watchlistIds.value;
    const rows = filterHomeFeedRows(storeRefs.rawRows.value, storeRefs.primaryCategory.value);
    const mapped = mapFeedCards(rows, t, te, { locale: locale.value });
    if (mapped.length) {
      feedCardsCache.value = mapped;
    }
    return enrichFeedCardsForDisplay(mapped).map((card) => ({
      ...card,
      watchlisted: isWatchlisted(card.id),
    }));
  });

  /**
   * 优先展示当前映射；否则回退缓存。
   * 默认「全部市场」视图下即使 rawRows  transient 为空也保留上次卡片（zoom/误触不会变空态）。
   */
  const displayCards = computed(() => {
    if (cards.value.length) return cards.value;
    if (!feedCardsCache.value.length) return cards.value;

    if (storeRefs.listLoading.value || storeRefs.loadingMore.value) {
      return feedCardsCache.value;
    }
    if (storeRefs.rawRows.value.length > 0) {
      return feedCardsCache.value;
    }
    if (isDefaultFeedView(storeRefs)) {
      return feedCardsCache.value;
    }
    return cards.value;
  });

  const showFeedEmpty = computed(() => {
    const err = storeRefs.listFetchError.value;
    if ((err === 'network' || err === 'server') && !displayCards.value.length) return false;
    if (storeRefs.listLoading.value || storeRefs.loadingMore.value) return false;
    if (displayCards.value.length) return false;
    return storeRefs.showListEmpty.value;
  });

  const { showFeedNetworkError, showFeedFetchError, networkErrorRetrying, feedFetchErrorRetrying } =
    useFeedNetworkPresentation(storeRefs, displayCards);

  useFeedFavoriteSync(() =>
    storeRefs.rawRows.value.map((row) => String(row?.id ?? '').trim()).filter(Boolean)
  );

  const categoryChips = computed(() => {
    void locale.value;
    return buildHomeCategoryChips(storeRefs.categories.value, t);
  });

  const feedToolbarCategories = computed(() =>
    categoryChips.value
      .filter((chip) => chip.slug !== 'all')
      .map((chip) => ({ slug: chip.slug, name: chip.label }))
  );

  const emptyHint = computed(() => {
    void locale.value;
    const q = String(storeRefs.appliedQuestion.value ?? '').trim();
    if (q) return t('markets.searchEmpty', { q });
    return t('markets.empty');
  });

  return {
    feedStore,
    ...storeRefs,
    cards: displayCards,
    showFeedEmpty,
    showFeedNetworkError,
    showFeedFetchError,
    networkErrorRetrying,
    feedFetchErrorRetrying,
    categoryChips,
    feedToolbarCategories,
    emptyHint,
  };
}

/** 清空展示缓存（换号登录 resetAllFeedStores 时调用；主动登出不清，避免首页空白） */
export function clearFeedCardsCache() {
  feedCardsCache.value = [];
}
